import { type Duration, Effect } from 'effect';

type Fetch = (
  input: string | URL | Request,
  init?: RequestInit,
) => Promise<Response>;

const isSessionRequest = (input: string | URL | Request) =>
  new URL(
    input instanceof Request ? input.url : input,
    'http://relative.invalid',
  ).pathname.endsWith('/get-session');

/**
 * better-auth's session check has no timeout of its own: better-fetch starts
 * its timer only when the caller passes no abort signal, and the session
 * store always passes one. A `/get-session` that never answers would leave
 * the session pending forever, and everything waiting on it with it. This
 * bounds that one request — sign-in, sign-up and the rest keep their own
 * pace — and keeps better-auth's signal in charge of cancelling it.
 */
export const fetchWithSessionRequestTimeout =
  ({ fetch, timeout }: { fetch: Fetch; timeout: Duration.Input }): Fetch =>
  (input, init) =>
    isSessionRequest(input)
      ? Effect.runPromise(
          Effect.tryPromise((signal) => fetch(input, { ...init, signal })).pipe(
            Effect.timeout(timeout),
          ),
          { signal: init?.signal ?? undefined },
        )
      : fetch(input, init);
